/**
 * Platform marks for the install picker.
 *
 * Inline SVG rather than downloaded image files, for three reasons that all
 * point the same way: nothing is fetched at runtime so there is no request to
 * fail, they inherit `currentColor` so the active tab can tint them without a
 * second asset, and they stay sharp at any size on any display.
 *
 * The Apple and Windows marks are the real ones, drawn as paths. They are
 * trademarks of their owners and are used here only to say which operating
 * system a command is for, which is what they are for. The penguin is a plain
 * rendering of Tux rather than a trace of Larry Ewing's original, so there is
 * no artwork here whose licence has to travel with it.
 */

interface IconProps {
  /** Pixels. Matches the surrounding text rather than a fixed tile. */
  size?: number;
}

export function AppleIcon({ size = 16 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
    </svg>
  );
}

export function WindowsIcon({ size = 16 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      {/* The four-pane flag: two rows of two, the left column narrower. */}
      <path d="M0 3.449 9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699m10.949-8.099H24V24l-13.051-1.801" />
    </svg>
  );
}

/**
 * Tux, drawn from primitives rather than traced. Composed so it reads as a
 * penguin at 16px, which is the only size that matters here — a faithful
 * outline collapses into a smudge at this scale.
 */
export function LinuxIcon({ size = 16 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      {/* Feet first, so the body overlaps them rather than the other way round.
          Orange like the beak: in the silhouette colour they vanish into the
          body and the penguin reads as an egg. */}
      <ellipse cx="8.3" cy="21.4" rx="3.2" ry="1.7" fill="#f5a623" />
      <ellipse cx="15.7" cy="21.4" rx="3.2" ry="1.7" fill="#f5a623" />
      {/* Head and body as one silhouette. */}
      <path d="M12 1.4c-2.5 0-4.3 1.9-4.3 4.4 0 .9.2 1.6.5 2.3-1.6 1.9-2.6 4.4-2.9 6.9-.2 1.6-.1 3 .5 4.1.7 1.3 2 2 3.6 2.3 1.7.3 3.5.3 5.2 0 1.6-.3 2.9-1 3.6-2.3.6-1.1.7-2.5.5-4.1-.3-2.5-1.3-5-2.9-6.9.3-.7.5-1.4.5-2.3 0-2.5-1.8-4.4-4.3-4.4z" />
      {/* The belly, punched out so it takes the page's background. */}
      <ellipse cx="12" cy="15.4" rx="3.5" ry="4.2" fill="var(--bg, #fff)" />
      {/* Eyes and beak sit on top of the head silhouette. */}
      <ellipse cx="10.4" cy="5.4" rx="1.25" ry="1.6" fill="var(--bg, #fff)" />
      <ellipse cx="13.6" cy="5.4" rx="1.25" ry="1.6" fill="var(--bg, #fff)" />
      <circle cx="10.6" cy="5.6" r=".62" />
      <circle cx="13.4" cy="5.6" r=".62" />
      <path d="M12 6.7c1 0 1.85.55 1.85 1.2S13 9.1 12 9.1s-1.85-.55-1.85-1.2S11 6.7 12 6.7z" fill="#f5a623" />
    </svg>
  );
}
